/**
 * Flow map document: the one JSON shape every flow map host reads and writes.
 *
 * A flow map is a small directed graph of steps (nodes) joined by arrows
 * (edges). The page-builder widget keeps it in `props.flow`, the CRM keeps it in
 * the `CrmFlowMap.doc` column, and both hand it to the same <FlowMap> component.
 *
 * The shape follows React Flow's own serialisation (`id`, `position`, `source`,
 * `target`, `parentId`) so an export opens in any React Flow tool, but the
 * content sits flat on the node instead of in React Flow's free-form `data` bag.
 * A typed document is what lets the server reject a broken map and the table
 * view list it without guessing.
 *
 * Node ids are LOCAL names inside one document (edges point at them), not global
 * identities. Anything that reseeds block ids must leave a flow document alone:
 * see `isFlowDocShape` in model.ts and the guard in registry.ts `reseedIds`.
 *
 * React-free on purpose: the API imports this (through model.ts) to validate a
 * save, and the widget's static render uses it on the server.
 */
import type { LocalizedString } from "@wa/core";

export const FLOW_DOC_VERSION = 1 as const;

/** Text on a map: a plain string (CRM) or a per-locale map (page builder). */
export type FlowText = LocalizedString;

export const FLOW_NODE_KINDS = ["start", "step", "decision", "end", "note", "group"] as const;
export type FlowNodeKind = (typeof FLOW_NODE_KINDS)[number];

export const FLOW_TONES = ["neutral", "primary", "success", "warning", "danger", "info"] as const;
export type FlowTone = (typeof FLOW_TONES)[number];

/** How an arrow reads: a plain arrow, the yes / no branch of a decision, or an optional path. */
export const FLOW_EDGE_KINDS = ["default", "yes", "no", "optional"] as const;
export type FlowEdgeKind = (typeof FLOW_EDGE_KINDS)[number];

/** Connection points on a node: top, right, bottom, left. */
export const FLOW_HANDLES = ["t", "r", "b", "l"] as const;
export type FlowHandle = (typeof FLOW_HANDLES)[number];

/** Main reading direction: top to bottom, or left to right. */
export const FLOW_DIRECTIONS = ["TB", "LR"] as const;
export type FlowDirection = (typeof FLOW_DIRECTIONS)[number];

export const FLOW_EDGE_STYLES = ["curved", "orthogonal"] as const;
export type FlowEdgeStyle = (typeof FLOW_EDGE_STYLES)[number];

/**
 * What a node can point at in the host's own records. "outcome" is a result
 * word rather than a row: the end of an AI call campaign's map names what the
 * call came to (a disposition such as "callback"), and the CRM reads it back.
 */
export const FLOW_REF_TYPES = ["milestone", "task", "guide", "project", "outcome"] as const;
export type FlowRefType = (typeof FLOW_REF_TYPES)[number];

export const FLOW_VIEWS = ["canvas", "table"] as const;
export type FlowView = (typeof FLOW_VIEWS)[number];

/**
 * How a host marks a node without changing the document: "error" for a step
 * the host cannot use as drawn (an AI call campaign's map that cannot be
 * followed), "active" for where something is right now (a rehearsed or live
 * call). Drawn on the canvas, the picture and the table alike.
 */
export const FLOW_NODE_MARKS = ["error", "active"] as const;
export type FlowNodeMark = (typeof FLOW_NODE_MARKS)[number];

/**
 * A choice of the host's own records a node may point at, offered in the node
 * panel for the kinds named: the CRM's AI call campaign lets an end node pick
 * the outcome of the call. The host names the field and every option, so the
 * widget carries no vocabulary of its own.
 */
export interface FlowRefChoices {
  type: FlowRefType;
  kinds: readonly FlowNodeKind[];
  /** The field's label. */
  label: string;
  /** The option that clears the pointer. */
  noneLabel: string;
  options: ReadonlyArray<{ id: string; label: string }>;
}

export interface FlowPoint {
  x: number;
  y: number;
}

export interface FlowSize {
  width: number;
  height: number;
}

/** One line of a node's nested checklist. */
export interface FlowStep {
  id: string;
  text: FlowText;
  done?: boolean;
}

/** A link out of a node: a URL or app path, a jump to another node in the same map, or both. */
export interface FlowLink {
  id: string;
  label: FlowText;
  /** "/guides/onboarding", "https://...", "mailto:...", "tel:..." or "#anchor". */
  href?: string;
  /** A node in the same map to jump to. */
  nodeId?: string;
}

/** A pointer into the host's own records (a CRM milestone, task, guide or project) or a result word. */
export interface FlowRef {
  type: FlowRefType;
  id: string;
}

export interface FlowNode {
  id: string;
  kind: FlowNodeKind;
  /** Top-left corner in canvas units; relative to the parent group when `parentId` is set. */
  position: FlowPoint;
  title: FlowText;
  description?: FlowText;
  tone?: FlowTone;
  steps?: FlowStep[];
  links?: FlowLink[];
  /** The group this node sits in. Groups themselves never have a parent (one level only). */
  parentId?: string;
  /** Groups only: the box size. */
  size?: FlowSize;
  ref?: FlowRef;
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  /** Missing handles follow the map's direction (TB: bottom to top, LR: right to left). */
  sourceHandle?: FlowHandle;
  targetHandle?: FlowHandle;
  label?: FlowText;
  kind?: FlowEdgeKind;
}

export interface FlowMapDoc {
  version: typeof FLOW_DOC_VERSION;
  direction?: FlowDirection;
  edgeStyle?: FlowEdgeStyle;
  nodes: FlowNode[];
  edges: FlowEdge[];
}

/** Hard caps. The normaliser trims to them, the validator reports them, the API enforces them. */
export const FLOW_LIMITS = {
  nodes: 400,
  edges: 800,
  stepsPerNode: 40,
  linksPerNode: 12,
  title: 200,
  text: 2000,
  label: 120,
  href: 2048,
  coord: 100_000,
  sizeMin: 80,
  sizeMax: 4000,
} as const;

/**
 * The box each node kind is drawn at. The live canvas renders nodes at these
 * widths (height grows with content), and the static picture, the auto layout
 * and the starter map all place nodes by these numbers, so the three agree.
 */
export const FLOW_NODE_SIZE: Readonly<Record<FlowNodeKind, FlowSize>> = {
  start: { width: 180, height: 56 },
  step: { width: 240, height: 96 },
  decision: { width: 220, height: 104 },
  end: { width: 180, height: 56 },
  note: { width: 220, height: 96 },
  group: { width: 520, height: 320 },
};

/**
 * Every piece of UI copy the <FlowMap> component renders, as plain strings.
 * The widget fills it from its own bilingual table (labels.ts, `flowMapLabels`);
 * a host with its own catalogue (the CRM) may override any key.
 */
export interface FlowMapLabels {
  view: string;
  canvasView: string;
  tableView: string;
  add: string;
  autoLayout: string;
  directionTB: string;
  directionLR: string;
  fitView: string;
  zoomIn: string;
  zoomOut: string;
  undo: string;
  redo: string;
  deleteSelected: string;
  kindStart: string;
  kindStep: string;
  kindDecision: string;
  kindEnd: string;
  kindNote: string;
  kindGroup: string;
  toneNeutral: string;
  tonePrimary: string;
  toneSuccess: string;
  toneWarning: string;
  toneDanger: string;
  toneInfo: string;
  edgeDefault: string;
  edgeYes: string;
  edgeNo: string;
  edgeOptional: string;
  inspector: string;
  title: string;
  description: string;
  kind: string;
  tone: string;
  steps: string;
  addStep: string;
  stepPlaceholder: string;
  links: string;
  addLink: string;
  linkLabel: string;
  linkHref: string;
  linkNode: string;
  linkInvalid: string;
  noNode: string;
  parent: string;
  noParent: string;
  connections: string;
  addConnection: string;
  /** The search box on a long pick list (nodes, groups) and the line under a search that kept nothing. */
  pickSearch: string;
  pickNoMatch: string;
  target: string;
  edgeLabel: string;
  edgeKind: string;
  deleteNode: string;
  deleteEdge: string;
  /** Removes one sub-step or one link from the node being edited. */
  remove: string;
  close: string;
  openLink: string;
  goToNode: string;
  untitled: string;
  colOrder: string;
  colTitle: string;
  colKind: string;
  colFrom: string;
  colTo: string;
  colSteps: string;
  colLinks: string;
  /** Contains "{column}", replaced with the column name. */
  sortBy: string;
  emptyTitle: string;
  emptyHint: string;
  emptyReadOnly: string;
  canvasHint: string;
  loading: string;
  /**
   * Screen reader copy, handed to React Flow (`ariaLabelConfig`). These are read
   * aloud and never drawn, which is exactly why they were the last English text
   * left in the map. Only the parts the map actually renders are here: it draws
   * its own toolbar, so React Flow's Controls panel is never on screen and its
   * labels are not translated.
   */
  a11yNode: string;
  a11yNodeMove: string;
  /** Contains "{x}" and "{y}", replaced with the step's new position. */
  a11yNodeMoved: string;
  a11yEdge: string;
  a11yMinimap: string;
  a11yHandle: string;
  nodesCount: (n: number) => string;
  edgesCount: (n: number) => string;
  stepsProgress: (done: number, total: number) => string;
}
